import React, { useState, useMemo } from "react";
import Papa from "papaparse";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Typography,
  TablePagination,
  TableContainer,
  Paper,
  Box,
  TextField,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const normalizeKey = (key) =>
  key
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .toLowerCase()
    .trim();

const getValue = (row, keys) => {
  for (let k of keys) if (row[k] !== undefined) return row[k];
  return null;
};

const isValidTime = (v) =>
  v &&
  !["", "-", "null", "------"].includes(
    v.toString().trim().toLowerCase()
  );

const calculateWorkedHours = (row) => {
  const toMin = (t) => {
    if (!t?.includes(":")) return null;

    const [h, m] = t.split(":").map(Number);

    return h * 60 + m;
  };

  const start = toMin(
    getValue(row, ["arrivee", "arrive"])
  );

  const end = toMin(
    getValue(row, ["depart", "dpart"])
  );

  if (start !== null && end !== null && end > start)
    return (end - start) / 60;

  return 0;
};

export default function PresencePage() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [saveMonth, setSaveMonth] = useState("");
  const [holidayDays, setHolidayDays] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [employeeDays, setEmployeeDays] = useState([]);

  // ============================================================
  // IMPORT CSV
  // ============================================================

  const handleFile = (e) => {
    const file = e.target.files[0];

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const cleaned = res.data.map((row) => {
          const obj = {};

          Object.keys(row).forEach((k) => {
            obj[normalizeKey(k)] = row[k];
          });

          return obj;
        });

        setData(cleaned);
      },
    });
  };

  // ============================================================
  // HEURES NORMALES SELON LE JOUR
  // Lundi -> Vendredi : 8h
  // Samedi : 5h
  // Dimanche : 0h
  // ============================================================

  const getExpectedHoursByDate = (date) => {
    const day = new Date(date).getDay();

    // Dimanche
    if (day === 0)
      return 0;

    // Samedi
    if (day === 6)
      return 5;

    // Lundi - Vendredi
    return 8;
  };

  // ============================================================
  // DETAILS EMPLOYE
  // ============================================================

  const showEmployeeDetails = (employeeName) => {
    const daysMap = {};

    data.forEach((row) => {
      const emp = row.nom?.trim() || "Inconnu";

      if (emp !== employeeName || !row.date) return;

      const dateKey = new Date(row.date)
        .toISOString()
        .split("T")[0];

      const arrivee = getValue(row, [
        "arrivee",
        "arrive",
      ]);

      const depart = getValue(row, [
        "depart",
        "dpart",
      ]);

      const typeAbsence = getValue(row, [
        "type dabsence",
        "type dabsence",
      ]);

      if (!daysMap[dateKey]) {
        daysMap[dateKey] = {
          date: dateKey,
          worked: 0,
          arrivee,
          depart,
          typeAbsence: formatAbsence(typeAbsence),
        };
      }

      daysMap[dateKey].worked +=
        calculateWorkedHours(row);
    });

    const details = Object.values(daysMap).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    setSelectedEmployee(employeeName);
    setEmployeeDays(details);
    setDetailsOpen(true);
  };

  // ============================================================
  // FORMAT ABSENCE
  // ============================================================

  const formatAbsence = (value) => {
    if (!value) return "-";

    const normalized = value
      .toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    // Jour férié
    if (
      normalized.includes("ferie") ||
      normalized.includes("férié") ||
      normalized.includes("jour ferie")
    ) {
      return "Jour férié";
    }

    // Congés
    if (
      normalized.includes("personnel") ||
      normalized.includes("maladie") ||
      normalized.includes("conge")
    ) {
      return "Congé";
    }

    return value;
  };

  // ============================================================
  // STATS
  // ============================================================

  const stats = useMemo(() => {
    const holidayDates = new Set(
      data
        .filter(
          (row) =>
            formatAbsence(
              getValue(row, ["type dabsence"])
            ) === "Jour férié"
        )
        .map(
          (row) =>
            new Date(row.date)
              .toISOString()
              .split("T")[0]
        )
    );

    const employees = {};
    const result = {};
    let absenceTypes = {};

    data.forEach((row) => {
      if (!row.date) return;

      const date = new Date(row.date);

      const key = date
        .toISOString()
        .split("T")[0];

      const emp = row.nom?.trim() || "Inconnu";

      const absence = getValue(row, [
        "type dabsence",
      ]);

      if (absence && absence !== "-") {
        if (!absenceTypes[emp])
          absenceTypes[emp] = {};

        const absenceClean =
          formatAbsence(absence);

        if (absenceClean === "Jour férié") {
          holidayDates.add(key);
        }

        absenceTypes[emp][absenceClean] =
          (absenceTypes[emp][absenceClean] || 0) + 1;
      }

      if (!employees[emp])
        employees[emp] = {};

      if (!employees[emp][key]) {
        employees[emp][key] = {
          worked: 0,
          hasEntry: false,
          hasExit: false,
          day: date.getDay(),
        };
      }

      const arr = getValue(row, [
        "arrivee",
        "arrive",
      ]);

      const dep = getValue(row, [
        "depart",
        "dpart",
      ]);

      if (isValidTime(arr))
        employees[emp][key].hasEntry = true;

      if (isValidTime(dep))
        employees[emp][key].hasExit = true;

      employees[emp][key].worked +=
        calculateWorkedHours(row);
    });

    // ============================================================
    // CALCUL PAR EMPLOYE
    // ============================================================

    Object.entries(employees).forEach(
      ([emp, days]) => {
        let worked = 0;
        let expected = 0;
        let absent = 0;
        let missing = 0;
        let penalty = 0;
        let workedDays = 0;

        let leaveDays = 0;
        let holidayDays = 0;
        let realAbsentDays = 0;

        // NOUVEAU :
        // heures de congés
        let leaveHours = 0;

        let holidayHours = 0;

        // ========================================================
        // JOURS FERIES DE L'EMPLOYE
        // ========================================================

        const employeeHolidayDays =
          Object.keys(days).filter(
            (date) => holidayDates.has(date)
          );

        // ========================================================
        // HEURES JOURS FERIES
        // ========================================================

        employeeHolidayDays.forEach((date) => {
          holidayHours +=
            getExpectedHoursByDate(date);
        });

        // ========================================================
        // CALCUL PAR JOUR
        // ========================================================

        Object.entries(days).forEach(
          ([dateKey, d]) => {
            const isWeekday =
              d.day >= 1 && d.day <= 5;

            // ----------------------------------------------------
            // TRAVAIL REEL
            // ----------------------------------------------------

            if (isWeekday) {
              worked += Math.max(
                0,
                d.worked - 1
              );
            } else {
              worked += d.worked;
            }

            // ----------------------------------------------------
            // JOURS TRAVAILLES
            // ----------------------------------------------------

            if (
              d.hasEntry ||
              d.hasExit ||
              d.worked > 0
            ) {
              workedDays++;
            }

            // ----------------------------------------------------
            // JOURS OUVRABLES
            // ----------------------------------------------------

            if (d.day !== 0) {
              const dailyExpected =
                d.day === 6 ? 5 : 8;

              expected += dailyExpected;

              // --------------------------------------------------
              // RECHERCHE DE L'ABSENCE
              // --------------------------------------------------

              const rowForDate =
                data.find(
                  (r) =>
                    r.nom?.trim() === emp &&
                    new Date(r.date)
                      .toISOString()
                      .split("T")[0] ===
                    dateKey
                );

              const absenceType =
                formatAbsence(
                  getValue(
                    rowForDate,
                    ["type dabsence"]
                  )
                );

              // ==================================================
              // CONGE
              // Lundi -> Vendredi = 8h
              // Samedi = 5h
              // Dimanche = 0h
              // ==================================================

              if (absenceType === "Congé") {
                leaveHours +=
                  getExpectedHoursByDate(
                    dateKey
                  );
              }

              // --------------------------------------------------
              // JOUR FERIE
              // --------------------------------------------------

              if (
                absenceType === "Jour férié"
              ) {
                holidayDays++;
              }

              // --------------------------------------------------
              // CONGE
              // --------------------------------------------------

              else if (
                absenceType === "Congé"
              ) {
                leaveDays++;
              }

              // --------------------------------------------------
              // ABSENCE NORMALE
              // --------------------------------------------------

              else if (
                !d.hasEntry &&
                !d.hasExit &&
                d.worked === 0
              ) {
                realAbsentDays++;
                absent++;
              }

              // --------------------------------------------------
              // INCOMPLET
              // --------------------------------------------------

              const isIncomplete =
                (d.hasEntry && !d.hasExit) ||
                (!d.hasEntry && d.hasExit);

              // --------------------------------------------------
              // ABSENCE REELLE
              // --------------------------------------------------

              const isRealAbsence =
                !d.hasEntry &&
                !d.hasExit &&
                d.worked === 0;

              // --------------------------------------------------
              // PENALITE
              // --------------------------------------------------

              if (isIncomplete) {
                if (
                  absenceType !== "Congé" &&
                  absenceType !== "Jour férié"
                ) {
                  missing++;
                  penalty += dailyExpected;
                }
              }
            }
          }
        );

        // ========================================================
        // ABSENCES CORRIGEES
        // ========================================================

        const correctedAbsent =
          Math.max(
            0,
            absent - employeeHolidayDays.length
          );

        // ========================================================
        // TOTAL
        //
        // AVANT :
        // worked + penalty + holidayHours
        //
        // MAINTENANT :
        // worked + leaveHours + penalty + holidayHours
        // ========================================================

        const totalSimulated =
          worked +
          leaveHours +
          penalty +
          holidayHours;

        result[emp] = {
          worked,
          expected,
          absent,

          absenceTypes:
            absenceTypes[emp] || {},

          correctedAbsent,

          missing,
          penalty,

          holidayHours,

          workedDays,

          totalSimulated,

          holidayDays:
            employeeHolidayDays.length,

          leaveDays,

          holidayDays,

          realAbsentDays,

          // NOUVEAU
          leaveHours,
        };
      }
    );

    return result;
  }, [data]);

  // ============================================================
  // FILTER
  // ============================================================

  const filtered = Object.entries(stats)
    .filter(([_, v]) => v.worked > 0)
    .filter(([e]) =>
      e
        .toLowerCase()
        .includes(search.toLowerCase())
    );

  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ============================================================
  // UI COLOR SYSTEM
  // ============================================================

  const getRowColor = (v) => {
    if (v.totalSimulated > 160)
      return "#e8f5e9";

    if (v.penalty > 0)
      return "#fff3e0";

    if (v.correctedAbsent > 0)
      return "#ffebee";

    return "white";
  };

  // ============================================================
  // HEURES AJUSTEES
  // ============================================================

  const getAdjustedHours = (d) => {
    const day =
      new Date(d.date).getDay();

    const isWeekday =
      day >= 1 && day <= 5;

    return isWeekday
      ? Math.max(0, d.worked - 1)
      : d.worked;
  };

  // ============================================================
  // EXPORT PDF EMPLOYE
  // ============================================================

  const exportEmployeePDF = (
    employeeName
  ) => {
    const employeeStat =
      stats[employeeName];

    if (!employeeStat) return;

    // ==========================================================
    // GET EMPLOYEE DETAILS
    // ==========================================================

    const daysMap = {};

    data.forEach((row) => {
      const emp =
        row.nom?.trim() || "Inconnu";

      if (
        emp !== employeeName ||
        !row.date
      )
        return;

      const dateKey =
        new Date(row.date)
          .toISOString()
          .split("T")[0];

      const arrivee = getValue(row, [
        "arrivee",
        "arrive",
      ]);

      const depart = getValue(row, [
        "depart",
        "dpart",
      ]);

      const typeAbsence =
        getValue(row, [
          "type dabsence",
        ]);

      if (!daysMap[dateKey]) {
        daysMap[dateKey] = {
          date: dateKey,
          arrivee,
          depart,
          worked: 0,
          typeAbsence:
            formatAbsence(
              typeAbsence
            ),
        };
      }

      daysMap[dateKey].worked +=
        calculateWorkedHours(row);
    });

    const details =
      Object.values(daysMap).sort(
        (a, b) =>
          new Date(a.date) -
          new Date(b.date)
      );

    // ==========================================================
    // CREATE PDF
    // ==========================================================

    const doc =
      new jsPDF("portrait");

    // ==========================================================
    // HEADER
    // ==========================================================

    doc.setFillColor(
      25,
      118,
      210
    );

    doc.rect(
      0,
      0,
      210,
      25,
      "F"
    );

    doc.setTextColor(255);
    doc.setFontSize(16);

    doc.text(
      "DETAIL PRESENCE EMPLOYE",
      14,
      16
    );

    doc.setTextColor(0);
    doc.setFontSize(11);

    doc.text(
      `Employé : ${employeeName}`,
      14,
      35
    );

    doc.text(
      `Mois : ${saveMonth}`,
      14,
      42
    );

    // ==========================================================
    // SUMMARY TABLE
    // ==========================================================

    autoTable(doc, {
      startY: 50,

      head: [[
        "Jours travaillés",
        "Travail",
        "Normal",
        "Congés",
        "Fériés",
        "Absences réelles",
        "Incomplets",
        "Pénalité",
        "Total",

      ]],

      body: [[
        `${employeeStat.workedDays} j`,

        `${employeeStat.worked.toFixed(
          2
        )} h`,

        `${employeeStat.expected} h`,

        `${employeeStat.leaveDays} j (${employeeStat.leaveHours}h)`,

        `${employeeStat.holidayDays} j (${employeeStat.holidayHours}h)`,

        `${employeeStat.realAbsentDays} j`,

        employeeStat.missing,

        `${employeeStat.penalty} h`,

        `${employeeStat.totalSimulated.toFixed(
          2
        )} h`,
      ]],

      styles: {
        fontSize: 9,
        halign: "center",
      },
    });

    // ==========================================================
    // DAILY DETAILS
    // ==========================================================

    autoTable(doc, {
      startY: 90,

      head: [[
        "Date",
        "Arrivée",
        "Départ",
        "Statut",
        "Heures travaillées",
      ]],

      body: details.map((d) => {
        const isSunday =
          new Date(d.date).getDay() === 0;

        return [
          new Date(d.date)
            .toLocaleDateString(
              "fr-FR"
            ) +
          (isSunday
            ? " (Dimanche)"
            : ""),

          d.arrivee || "-",

          d.depart || "-",

          (() => {
            const status =
              formatAbsence(
                d.typeAbsence
              );

            if (
              status === "Congé"
            )
              return "Congé";

            if (
              status === "Jour férié"
            )
              return "Jour férié";

            if (
              !d.arrivee &&
              !d.depart &&
              d.worked === 0
            )
              return "Absence réelle";

            return "-";
          })(),

          `${getAdjustedHours(
            d
          ).toFixed(2)} h`,
        ];
      }),

      styles: {
        fontSize: 9,
        halign: "center",
      },

      headStyles: {
        fillColor: [
          15,
          23,
          42,
        ],
        textColor: 255,
      },

      // ========================================================
      // COLOR ROWS
      // ========================================================

      didParseCell: (cell) => {
        const row =
          details[cell.row.index];

        if (!row) return;

        if (
          row.typeAbsence ===
          "Jour férié"
        ) {
          cell.cell.styles.fillColor =
            [
              200,
              230,
              201,
            ];
        }

        if (
          row.typeAbsence ===
          "Congé"
        ) {
          cell.cell.styles.fillColor =
            [
              200,
              230,
              201,
            ];
        }

        if (
          new Date(
            row.date
          ).getDay() === 0
        ) {
          cell.cell.styles.fillColor =
            [
              255,
              205,
              210,
            ];
        }

        if (
          cell.section !== "body"
        )
          return;

        const rowData =
          details[
          cell.row.index
          ];

        if (!rowData)
          return;

        const date =
          new Date(
            rowData.date
          );

        if (
          date.getDay() === 0
        ) {
          cell.cell.styles.fillColor =
            [
              255,
              205,
              210,
            ];
        }
      },
    });

    // ==========================================================
    // SAVE
    // ==========================================================

    doc.save(
      `presence_${employeeName}_${saveMonth}.pdf`
    );
  };

  // ============================================================
  // PDF GLOBAL
  // ============================================================

  const exportPDF = () => {
    if (!saveMonth) return;

    const doc =
      new jsPDF("landscape");

    // ==========================================================
    // HEADER
    // ==========================================================

    doc.setFillColor(
      25,
      118,
      210
    );

    doc.rect(
      0,
      0,
      300,
      25,
      "F"
    );

    doc.setTextColor(255);
    doc.setFontSize(18);

    doc.text(
      "RAPPORT RH - PRESENCE | Régime 45H/S",
      14,
      16
    );

    doc.setTextColor(0);
    doc.setFontSize(11);

    doc.text(
      `Mois: ${saveMonth}`,
      14,
      35
    );

    // ==========================================================
    // TABLE
    // ==========================================================

    autoTable(doc, {
      startY: 45,

      head: [[
        "Employé",
        "Jours",
        "Travail",
        "Normal",
        "Congés",
        "Fériés",
        "Absences",
        "Incomplets",
        "Pénalité",
        "Total",
        "Différence",
      ]],

     body: filtered.map(
  ([e, v]) => [
    e,
    `${v.workedDays} j`,
    `${v.worked.toFixed(1)} h`,
    `${v.expected} h`,
    `${v.leaveDays} j (${v.leaveHours}h)`,
    `${v.holidayDays} j (${v.holidayHours}h)`,
    `${v.realAbsentDays} j`,
    v.missing,
    `${v.penalty} h`,
    `${v.totalSimulated.toFixed(1)} h`,
    `${(
      v.totalSimulated - v.expected
    ).toFixed(1)} h`,
  ]
),
      styles: {
        fontSize: 9,
        halign: "center",
      },

      headStyles: {
        fillColor: [
          15,
          23,
          42,
        ],
        textColor: 255,
      },

      // ========================================================
      // SAME COLOR LOGIC AS UI
      // ========================================================

      didParseCell: (data) => {
        if (
          data.section !==
          "body"
        )
          return;

        const v =
          filtered[
          data.row.index
          ][1];

        // Absences
        if (
          data.column.index ===
          4
        ) {
          data.cell.styles.fillColor =
            v.correctedAbsent > 0
              ? [
                255,
                200,
                200,
              ]
              : [
                200,
                255,
                200,
              ];
        }

        // Fériés
        if (
          data.column.index ===
          5
        ) {
          data.cell.styles.fillColor =
            [
              180,
              220,
              255,
            ];
        }

        // Pénalité
        if (
          data.column.index ===
          7
        ) {
          data.cell.styles.fillColor =
            v.penalty > 0
              ? [
                255,
                180,
                120,
              ]
              : [
                220,
                255,
                220,
              ];
        }

        // Total
        if (
          data.column.index ===
          9
        ) {
          data.cell.styles.fillColor =
            [
              220,
              200,
              255,
            ];
        }
      },
    });

    doc.save(
      `rapport_${saveMonth}.pdf`
    );
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <Box p={3}>

      <Typography
        variant="h4"
        mb={3}
      >
        Rapport RH
      </Typography>

      {/* ======================================================
          CONTROLS
      ====================================================== */}

      <Box
        display="flex"
        gap={2}
        flexWrap="wrap"
        mb={2}
      >

        <Button
          variant="contained"
          component="label"
        >
          Import CSV

          <input
            hidden
            type="file"
            onChange={handleFile}
          />
        </Button>

        <TextField
          type="month"
          value={saveMonth}
          onChange={(e) =>
            setSaveMonth(
              e.target.value
            )
          }
        />

        <TextField
          label="Recherche"
          value={search}
          onChange={(e) =>
            setSearch(
              e.target.value
            )
          }
        />

        <Button
          variant="contained"
          color="error"
          startIcon={
            <PictureAsPdfIcon />
          }
          onClick={exportPDF}
          disabled={!saveMonth}
        >
          Export PDF
        </Button>

      </Box>

      {/* ======================================================
          TABLE
      ====================================================== */}

      <TableContainer
        component={Paper}
      >
        <Table>

          <TableHead>
            <TableRow>
              {[
                "Employé",
                "Jours travaillés",
                "Travail",
                "Normal",
                "Congés",
                "Fériés",
                "Absences réelles",
                "Types absence",
                "Incomplets",
                "Pénalité",
                "Total",
                "Différence",
                "Actions",
              ].map((h) => (
                <TableCell key={h}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>

            {paginated.map(
              ([e, v]) => (
                <TableRow
                  key={e}
                  sx={{
                    backgroundColor:
                      getRowColor(v),
                  }}
                >

                  <TableCell>
                    {e}
                  </TableCell>

                  <TableCell>
                    {v.workedDays} j
                  </TableCell>

                  <TableCell>
                    {v.worked.toFixed(
                      1
                    )} h
                  </TableCell>

                  <TableCell>
                    {v.expected} h
                  </TableCell>

                  {/* =================================================
                      CONGES
                      Nombre de jours + heures
                  ================================================= */}

                  <TableCell>
                    {v.leaveDays} j
                    {" "}
                    ({v.leaveHours} h)
                  </TableCell>

                  {/* =================================================
                      FERIES
                  ================================================= */}

                  <TableCell>
                    {v.holidayDays} j
                  </TableCell>

                  <TableCell>
                    {v.realAbsentDays} j
                  </TableCell>

                  <TableCell>
                    {Object.entries(
                      v.absenceTypes || {}
                    ).map(
                      ([type, count]) => (
                        <Chip
                          key={type}
                          label={`${type} (${count})`}
                          size="small"
                          sx={{
                            mr: 0.5,
                          }}
                        />
                      )
                    )}
                  </TableCell>

                  <TableCell>
                    {v.missing}
                  </TableCell>

                  <TableCell>
                    {v.penalty}h
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={`${(
                        v.totalSimulated ||
                        0
                      ).toFixed(1)} h`}
                      color="secondary"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${(
                        v.totalSimulated - v.expected
                      ).toFixed(1)} h`}
                      color={
                        v.totalSimulated - v.expected > 0
                          ? "success"
                          : v.totalSimulated - v.expected < 0
                            ? "error"
                            : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>

                    {/* VOIR DETAILS */}

                    <IconButton
                      color="primary"
                      title="Voir les heures"
                      onClick={() =>
                        showEmployeeDetails(
                          e
                        )
                      }
                    >
                      <AccessTimeIcon />
                    </IconButton>

                    {/* EXPORT INDIVIDUEL */}

                    <IconButton
                      color="secondary"
                      title="Exporter PDF employé"
                      onClick={() =>
                        exportEmployeePDF(
                          e
                        )
                      }
                    >
                      <DownloadIcon />
                    </IconButton>

                  </TableCell>

                </TableRow>
              )
            )}

          </TableBody>

        </Table>
      </TableContainer>

      {/* ======================================================
          PAGINATION
      ====================================================== */}

      <TablePagination
        count={filtered.length}
        page={page}
        rowsPerPage={
          rowsPerPage
        }
        onPageChange={(e, p) =>
          setPage(p)
        }
        onRowsPerPageChange={(e) => {
          setRowsPerPage(
            +e.target.value
          );
          setPage(0);
        }}
      />

      {/* ======================================================
          DIALOG DETAILS
      ====================================================== */}

      <Dialog
        open={detailsOpen}
        onClose={() =>
          setDetailsOpen(false)
        }
        maxWidth="md"
        fullWidth
      >

        <DialogTitle>
          Heures travaillées -{" "}
          {selectedEmployee}
        </DialogTitle>

        <DialogContent>

          <Table size="small">

            <TableHead>
              <TableRow>

                <TableCell>
                  Date
                </TableCell>

                <TableCell>
                  Arrivée
                </TableCell>

                <TableCell>
                  Départ
                </TableCell>

                <TableCell>
                  Type absence
                </TableCell>

                <TableCell align="right">
                  Heures
                </TableCell>

              </TableRow>
            </TableHead>

            <TableBody>

              {employeeDays.map(
                (day) => {
                  const date =
                    new Date(
                      day.date
                    );

                  const isSunday =
                    date.getDay() === 0;

                  const isHoliday =
                    day.typeAbsence ===
                    "Jour férié";

                  const isLeave =
                    day.typeAbsence ===
                    "Congé";

                  return (
                    <TableRow
                      key={day.date}
                      sx={{
                        backgroundColor:
                          isHoliday ||
                            isLeave
                            ? "#C8E6C9"
                            : isSunday
                              ? "#FFEBEE"
                              : "inherit",
                      }}
                    >

                      <TableCell>
                        {date.toLocaleDateString(
                          "fr-FR"
                        )}

                        {isSunday && (
                          <Chip
                            label="Dimanche"
                            size="small"
                            color="error"
                            sx={{
                              ml: 1,
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        {day.arrivee ||
                          "-"}
                      </TableCell>

                      <TableCell>
                        {day.depart ||
                          "-"}
                      </TableCell>

                      <TableCell>
                        {day.typeAbsence ||
                          "-"}
                      </TableCell>

                      <TableCell align="right">
                        {getAdjustedHours(
                          day
                        ).toFixed(2)}{" "}
                        h
                      </TableCell>

                    </TableRow>
                  );
                }
              )}

              {/* =================================================
                  TOTAL DETAILS
              ================================================= */}

              <TableRow>

                <TableCell
                  colSpan={3}
                  align="right"
                >
                  <b>
                    Total
                  </b>
                </TableCell>

                <TableCell align="right">
                  <b>
                    {employeeDays
                      .reduce(
                        (
                          sum,
                          d
                        ) =>
                          sum +
                          getAdjustedHours(
                            d
                          ),
                        0
                      )
                      .toFixed(2)}{" "}
                    h
                  </b>
                </TableCell>

              </TableRow>

            </TableBody>

          </Table>

        </DialogContent>

      </Dialog>

    </Box>
  );
}